import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import axios from "axios";
import { TbTrashX } from "react-icons/tb";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";
import ConfirmDialog from "./ConfirmDialog";
import PrintRequestPage from "./PrintRequestPage";

function RequestList() {
  const [record, setRecord] = useState([]);
  const { user } = useSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [tempArr, setTempArr] = useState([]);
  const [printData, setPrintData] = useState(null);

  useEffect(() => {
    getAllPending();
  }, []);

  const getAllPending = async () => {
    try {
      const response = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/transaction/pending/`
      );

      // ✅ Sort by transaction_time (latest first)
      const sorted = response.data.result.sort(
        (a, b) => new Date(b.transaction_time) - new Date(a.transaction_time)
      );

      setRecord(sorted);
    } catch (error) {
      console.error("Fetch failed:", error);
      toast.error("Failed to fetch records");
    }
  };

  function applyStripedStyle(index, data) {
    const isVoid =
      data.transaction_remark && data.transaction_remark.includes("[VOID]");
    return isVoid
      ? "bg-red-300"
      : index % 2 === 0
      ? "border-b bg-gray-50"
      : "bg-white";
  }

  const cancelRequest = async (data) => {
    try {
      setIsLoading(true);
      await axios.put(
        `https://eps.elkomprintama.com/api/store/stocks/cancel_request`,
        {
          groupId: data.transaction_group_id,
          approvedBy: user.username, // Optional: if you track who approved
          transaction_id: data.transaction_id,
        }
      );

      toast.success("Stock request approved!");
      getAllPending();
    } catch (error) {
      console.error("Approval failed:", error);
      toast.error(
        "Failed to cancel: " + (error?.response?.data?.msg || error.message)
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Group records by transaction_group_id
  const groupedData = record.reduce((acc, curr) => {
    const groupId = curr.transaction_group_id || "UNGROUPED";
    if (!acc[groupId]) {
      acc[groupId] = [];
    }
    acc[groupId].push(curr);
    return acc;
  }, {});

  // ✅ Sort groups by latest transaction_time inside each group
  const sortedGroups = Object.entries(groupedData).sort(([, a], [, b]) => {
    const latestA = Math.max(
      ...a.map((item) => new Date(item.transaction_time))
    );
    const latestB = Math.max(
      ...b.map((item) => new Date(item.transaction_time))
    );
    return latestB - latestA; // newest first
  });

  return (
    <div className="flex flex-col h-full bg-gray-200 px-5 py-5 sm:rounded-lg">
      <div className="control w-full">
        <div className="w-full rounded-lg bg-pink-500">
          <label className="block uppercase text-white mb-2 tracking-wide text-5xl text-center font-bold">
            Request List
          </label>
        </div>
      </div>

      <div className="relative max-h-[75vh] overflow-y-auto">
        <table className="text-sm w-full text-left text-gray-500 dark:text-gray-400 border-collapse">
          <thead className="sticky -top-8 text-sm text-center text-white uppercase bg-gray-600 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th className="whitespace-nowrap px-5 py-5 border-r">No</th>
              <th className="whitespace-nowrap px-5 border-r">Product Name</th>
              <th className="whitespace-nowrap px-5 border-r">Code</th>
              <th className="whitespace-nowrap px-5 border-r">Group</th>
              <th className="whitespace-nowrap px-5 border-r">Qty</th>
              <th className="whitespace-nowrap px-5 border-r">Type</th>
              <th className="whitespace-nowrap px-5 border-r">Customer</th>
              <th className="whitespace-nowrap px-5 border-r">Remark</th>
              <th className="whitespace-nowrap px-5 border-r">Input By</th>
              <th className="whitespace-nowrap px-5 border-r">Input Time</th>
              <th className="whitespace-nowrap px-5 border-r"></th>
            </tr>
          </thead>
          <tbody>
            {sortedGroups.length > 0 ? (
              sortedGroups.map(([groupId, items], groupIndex) => {
                // ✅ Sort items inside group by time desc
                const sortedItems = [...items].sort(
                  (a, b) =>
                    new Date(b.transaction_time) - new Date(a.transaction_time)
                );

                return (
                  <React.Fragment key={groupId}>
                    {/* Group Header */}
                    <tr className="bg-blue-300 text-black font-bold text-center">
                      <td colSpan="12" className="py-2 px-4">
                        Group ID: {groupId}
                      </td>
                    </tr>

                    {/* Grouped Items */}
                    {sortedItems.map((data, index) => (
                      <tr
                        key={`row-${groupId}-${index}`}
                        className={`border-b dark:bg-gray-900 dark:border-gray-700 hover:bg-blue-100 dark:hover:bg-gray-600 ${applyStripedStyle(
                          index,
                          data
                        )}`}
                      >
                        <td className="whitespace-nowrap px-5 py-5 border-r">
                          {data.form_id}
                        </td>
                        <td className="whitespace-nowrap px-5 border-r">
                          {data.product_name}
                        </td>
                        <td className="whitespace-nowrap px-5 border-r">
                          {data.product_code}
                        </td>
                        <td className="whitespace-nowrap px-5 border-r">
                          {data.group_name}
                        </td>
                        <td className="whitespace-nowrap px-5 border-r">
                          {data.stock_out}
                        </td>
                        <td
                          className={`whitespace-nowrap px-5 border-r ${
                            data.transaction_type === "Stock In"
                              ? "bg-green-200"
                              : data.transaction_type === "Pending"
                              ? "bg-yellow-200"
                              : "bg-red-200"
                          }`}
                        >
                          {data.transaction_type}
                        </td>
                        <td className="whitespace-nowrap px-5 border-r">
                          {data.transaction_for}
                        </td>
                        <td className="whitespace-nowrap px-5 border-r">
                          {data.transaction_remark}
                        </td>
                        <td className="whitespace-nowrap px-5 border-r">
                          {data.transaction_by}
                        </td>
                        <td className="whitespace-nowrap px-5 border-r">
                          {format(
                            new Date(data.transaction_time),
                            "dd-MM-yyyy HH:mm:ss"
                          )}
                        </td>
                        <td className="whitespace-nowrap px-2 border-r py-3">
                          <button
                            className="text-blue-500 hover:underline"
                            onClick={(e) => {
                              e.preventDefault();
                              const groupItems = record.filter(
                                (item) =>
                                  item.transaction_group_id ===
                                  data.transaction_group_id
                              );
                              setPrintData({
                                formId: data.form_id,
                                groupId: data.transaction_group_id,
                                items: groupItems,
                              });
                            }}
                          >
                            🖨️
                          </button>
                          <button
                            className="font-medium text-red-500 hover:underline px-3"
                            onClick={(e) => {
                              e.preventDefault();
                              setTempArr(data);
                              setShowConfirm(true);
                            }}
                          >
                            <TbTrashX size={"1.5em"} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan="12"
                  className="whitespace-nowrap px-5 py-5 text-center"
                >
                  <p className="text-gray-900">No data available</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {printData && (
        <PrintRequestPage
          formId={printData.formId}
          groupId={printData.groupId}
          items={printData.items}
          onDone={() => setPrintData(null)}
          useNow={false} // ✅ Use stored DB date
        />
      )}
      {isLoading && <LoadingOverlay message="loading..." />}
      {showConfirm && (
        <ConfirmDialog
          message="Cancel material? Jika sudah dicancel tidak bisa dibalik"
          onConfirm={() => {
            setShowConfirm(false);
            cancelRequest(tempArr);
          }}
          onCancel={() => {
            setShowConfirm(false);
            setTempArr([]);
          }}
        />
      )}
    </div>
  );
}

export default RequestList;
